import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Users, Eye, Key, Calendar, GraduationCap, 
    Video, Image, Mic, Zap, Shield, Globe, Rocket, 
    TrendingUp, BarChart, Settings, FileText
} from 'lucide-react';

/**
 * Apps Section with YouTube tools and community features
 * Auto-scrolling banner with multiple app icons - Premium design
 */
const AppsSection: React.FC = () => {
    const { t } = useTranslation();

    // Refined icon list with harmonious colors and consistent styling
    const appIcons = [
        { icon: Users, name: t('landing.apps.community', 'Cộng đồng'), bg: 'bg-slate-800/80', iconColor: 'text-purple-400' },
        { icon: Eye, name: t('landing.apps.checkView', 'Check View'), bg: 'bg-slate-800/80', iconColor: 'text-emerald-400' },
        { icon: Key, name: t('landing.apps.freeKeys', 'Free Keys'), bg: 'bg-slate-800/80', iconColor: 'text-orange-400' },
        { icon: Calendar, name: t('landing.apps.events', 'Sự kiện'), bg: 'bg-slate-800/80', iconColor: 'text-orange-400' },
        { icon: GraduationCap, name: t('landing.apps.classes', 'Lớp học'), bg: 'bg-slate-800/80', iconColor: 'text-white' },
        { icon: Video, name: 'Video Creator', bg: 'bg-slate-800/80', iconColor: 'text-white' },
        { icon: Image, name: 'Image Creator', bg: 'bg-slate-800/80', iconColor: 'text-pink-400' },
        { icon: Mic, name: 'Voice Clone', bg: 'bg-slate-800/80', iconColor: 'text-green-400' },
        { icon: Zap, name: 'AI Tools', bg: 'bg-slate-800/80', iconColor: 'text-yellow-400' },
        { icon: Shield, name: 'Security', bg: 'bg-slate-800/80', iconColor: 'text-white' },
        { icon: Globe, name: 'Explorer', bg: 'bg-slate-800/80', iconColor: 'text-blue-400' },
        { icon: Rocket, name: 'Launch', bg: 'bg-slate-800/80', iconColor: 'text-red-400' },
        { icon: TrendingUp, name: 'Analytics', bg: 'bg-slate-800/80', iconColor: 'text-green-400' },
        { icon: BarChart, name: 'Stats', bg: 'bg-slate-800/80', iconColor: 'text-blue-400' },
        { icon: Settings, name: 'Settings', bg: 'bg-slate-800/80', iconColor: 'text-white' },
        { icon: FileText, name: 'Transcript', bg: 'bg-slate-800/80', iconColor: 'text-white' },
    ];

    // Duplicate icons for seamless infinite scroll (3 sets)
    const duplicatedIcons = [...appIcons, ...appIcons, ...appIcons];

    return (
        <section className="relative py-16 px-4 sm:px-6 lg:px-8">
            {/* Subtle dark background with gentle gradient */}
            <div className="absolute inset-0 bg-slate-950">
                {/* Subtle vertical striped gradient - more refined and harmonious */}
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, rgba(251, 146, 60, 0.08) 0px, rgba(251, 146, 60, 0.08) 1px, transparent 1px, transparent 2px, rgba(59, 130, 246, 0.08) 2px, rgba(59, 130, 246, 0.08) 3px, transparent 3px, transparent 4px)',
                    backgroundSize: '4px 100%',
                }}></div>
                {/* Gentle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/95 to-slate-950"></div>
            </div>

            <div className="relative z-10 max-w-6xl mx-auto">
                {/* Features Bar with Compact Icons */}
                <div className="mb-12">
                    {/* Compact dark bar for Features label */}
                    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/50 mb-4 py-2.5 px-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <span className="text-white/80 text-xs font-medium uppercase tracking-wider whitespace-nowrap">
                                {t('landing.apps.label', 'Features')}:
                            </span>
                            <div className="flex-1"></div>
                        </div>
                    </div>
                    
                    {/* Compact Auto-scrolling Icons Banner */}
                    <div className="relative overflow-hidden bg-slate-900/40 backdrop-blur-sm border border-slate-800/30 rounded-lg py-3">
                        {/* Gradient fade on edges */}
                        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-900/40 to-transparent z-10 pointer-events-none"></div>
                        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-900/40 to-transparent z-10 pointer-events-none"></div>
                        
                        {/* Scrolling container - smooth infinite scroll */}
                        <div 
                            className="flex items-center gap-2.5 sm:gap-3 animate-scroll-horizontal"
                            style={{ width: 'fit-content' }}
                        >
                            {duplicatedIcons.map((app, index) => {
                                const IconComponent = app.icon;
                                return (
                                    <div
                                        key={`${app.name}-${index}`}
                                        className="group relative flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 cursor-pointer hover:scale-110 hover:-translate-y-1 transition-transform z-10 hover:z-20"
                                    >
                                        {/* Glow effect on hover */}
                                        <div className={`absolute inset-0 rounded-lg ${app.bg.replace('/80', '/40')} blur-md opacity-0 group-hover:opacity-100 transition-opacity`} />
                                        
                                        {/* Compact icon container with subtle border */}
                                        <div 
                                            className={`relative w-full h-full rounded-lg ${app.bg} flex items-center justify-center p-2 border border-white/10 shadow-sm backdrop-blur-sm overflow-hidden group-hover:border-white/35 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all`}
                                        >
                                            {/* Shimmer effect */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer-sweep" />
                                            
                                            <div className="relative z-10 group-hover:scale-110 transition-transform">
                                                <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 ${app.iconColor} drop-shadow-lg`} />
                                            </div>
                                        </div>
                                        
                                        {/* Enhanced Tooltip */}
                                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/95 backdrop-blur-sm text-white text-xs rounded-lg whitespace-nowrap z-30 shadow-xl border border-white/10 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all duration-200">
                                            {app.name}
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Compact TaoThaoAI Platform Button */}
                <div className="flex justify-center mb-10">
                    <div className="relative inline-block hover:-translate-y-1 transition-transform">
                        {/* Subtle outer glow */}
                        <div 
                            className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full opacity-60 blur-sm hover:blur-md hover:brightness-110 transition-all"
                        />
                        
                        {/* Main content wrapper - more refined */}
                        <div className="relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic">
                            {/* Inner pill body */}
                            <div className="flex items-center justify-center bg-black/80 rounded-full px-3.5 py-1.5 text-xs backdrop-blur-sm hover:bg-black/70 transition-colors">
                                <span className="text-white/90 font-medium hover:text-white transition-colors">
                                    {t('landing.apps.platformStack', 'TaoThaoAI Platform')}
                                </span>
                            </div>

                            {/* Subtle shimmer overlay */}
                            <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                                <div className="h-full w-full bg-gradient-to-r from-transparent via-white/15 to-transparent animate-shimmer-sweep"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content - refined spacing */}
                <div className="text-center">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                        {t('landing.apps.title', 'Được thiết kế cho YouTuber chuyên nghiệp')}
                    </h2>
                    <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
                        {t('landing.apps.description', 'TaoThaoAI cung cấp bộ công cụ AI mạnh mẽ nhất để tạo video, cộng đồng hỗ trợ phát triển kênh, và hệ sinh thái hoàn chỉnh giúp bạn trở thành YouTuber thành công.')}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default AppsSection;

